import {Trans} from '@ui/i18n/trans';
import {useSettings} from '@ui/settings/use-settings';
import {useEffect, useMemo, useState} from 'react';
import {useCookie} from '@ui/utils/hooks/use-cookie';
import {useLocation} from 'react-router-dom';

export function CookieNotice() {
  const {cookie_notice} = useSettings();
  const location = useLocation();

  const [cookie, setCookie] = useCookie('cookie_notice');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (cookie === 'true' || cookie === 'false') return;
    const t = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(t);
  }, [cookie]);

  const isAllowed = useMemo(
    () => ['/'].includes(location.pathname),
    [location.pathname],
  );

  if (!isAllowed || !cookie_notice?.enable || !visible) return null;

  const handle = (val: 'true' | 'false') => {
    setCookie(val, {days: 180, path: '/'});
    setVisible(false);
  };

  return (
    <>
      <style>{css}</style>

      <div
        role="dialog"
        aria-modal="false"
        aria-label="Cookie preferences"
        className="sb-cn-wrap"
      >
        <div className="sb-cn-card">
          {/* Header */}
          <div className="sb-cn-header">
            <div className="sb-cn-icon">
              <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="6.5" stroke="#00c6ff" strokeWidth="1" />
                <circle cx="6"   cy="6.5" r="1"    fill="#00c6ff" />
                <circle cx="9.5" cy="5"   r="0.75" fill="#0072ff" />
                <circle cx="10"  cy="9"   r="1"    fill="#00c6ff" />
                <circle cx="6.5" cy="10"  r="0.75" fill="#0072ff" />
              </svg>
            </div>
            <span className="sb-cn-title">
              <Trans message="Cookie preferences" />
            </span>
            <span className="sb-cn-badge">
              <Trans message="Privacy" />
            </span>
          </div>

          <div className="sb-cn-divider" />

          {/* Body */}
          <p className="sb-cn-desc">
            <Trans message='By clicking "Accept All", you are giving consent to cookies being used to enhance site navigation, analyze site usage, and assist in our marketing efforts. To manage your preferences, choose "Cookie Settings".' />
          </p>

          {/* Actions */}
          <div className="sb-cn-actions">
            <button
              className="sb-cn-btn sb-cn-btn--primary"
              onClick={() => handle('true')}
              autoFocus
            >
              <span><Trans message="Accept all" /></span>
            </button>
            <button
              className="sb-cn-btn sb-cn-btn--ghost"
              onClick={() => handle('false')}
            >
              <Trans message="Reject" />
            </button>
          </div>

          {/* Settings link */}
          {/* <div className="sb-cn-footer">
            <button className="sb-cn-settings">
              <Trans message="Manage cookie settings" />
            </button>
          </div> */}
        </div>
      </div>
    </>
  );
}

const css = `
  .sb-cn-wrap {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 9999;
  }

  .sb-cn-card {
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 340px;
    max-width: calc(100vw - 32px);
    background: #ffffff;
    border: 0.5px solid rgba(0, 114, 255, 0.18);
    border-radius: 14px;
    padding: 18px 18px 14px;
    pointer-events: auto;
    box-shadow:
      0 2px 8px rgba(0, 114, 255, 0.06),
      0 16px 40px rgba(0, 90, 200, 0.1);
    animation: sb-slide-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .sb-cn-card::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 15px;
    background: linear-gradient(135deg, rgba(0,114,255,0.12), rgba(0,198,255,0.08), transparent 55%);
    pointer-events: none;
    z-index: -1;
  }

  @keyframes sb-slide-up {
    from { opacity: 0; transform: translateY(12px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0)   scale(1);    }
  }

  .sb-cn-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  .sb-cn-icon {
    width: 32px; height: 32px; flex-shrink: 0;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(0,114,255,0.08), rgba(0,198,255,0.12));
    border: 0.5px solid rgba(0, 114, 255, 0.15);
    display: flex; align-items: center; justify-content: center;
  }
  .sb-cn-icon svg { width: 16px; height: 16px; }

  .sb-cn-title {
    font-size: 14px; font-weight: 500;
    color: #0a1628;
    letter-spacing: -0.01em;
  }

  .sb-cn-badge {
    margin-left: auto;
    font-size: 10px; font-weight: 500;
    color: #0059d6;
    background: rgba(0, 114, 255, 0.07);
    border: 0.5px solid rgba(0, 114, 255, 0.18);
    border-radius: 20px;
    padding: 2px 8px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .sb-cn-divider {
    height: 0.5px;
    background: rgba(0, 114, 255, 0.08);
    margin-bottom: 12px;
  }

  .sb-cn-desc {
    font-size: 12.5px;
    color: rgba(10, 22, 40, 0.52);
    line-height: 1.65;
    margin-bottom: 14px;
  }

  .sb-cn-actions { display: flex; gap: 8px; }

  .sb-cn-btn {
    border: none; border-radius: 8px;
    font-size: 12.5px; font-weight: 500;
    padding: 9px 12px; cursor: pointer;
    letter-spacing: -0.01em;
    transition: transform 0.1s, box-shadow 0.15s, background 0.15s, color 0.15s;
  }
  .sb-cn-btn:active { transform: scale(0.97); }
  .sb-cn-btn:focus-visible { outline: 2px solid #0072ff; outline-offset: 2px; }

  .sb-cn-btn--primary {
    flex: 1.4;
    background: linear-gradient(90deg, #0072ff, #00c6ff);
    color: #fff;
    position: relative; overflow: hidden;
    box-shadow: 0 4px 14px rgba(0, 114, 255, 0.28);
  }
  .sb-cn-btn--primary::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, #0059d6, #00aaff);
    opacity: 0; transition: opacity 0.2s; border-radius: 8px;
  }
  .sb-cn-btn--primary:hover::after { opacity: 1; }
  .sb-cn-btn--primary:hover { box-shadow: 0 6px 20px rgba(0, 114, 255, 0.38); }
  .sb-cn-btn--primary span { position: relative; z-index: 1; }

  .sb-cn-btn--ghost {
    flex: 1;
    background: rgba(0, 114, 255, 0.05);
    color: rgba(10, 22, 40, 0.55);
    border: 0.5px solid rgba(0, 114, 255, 0.15);
  }
  .sb-cn-btn--ghost:hover {
    background: rgba(0, 114, 255, 0.09);
    color: #0a1628;
  }

  .sb-cn-footer { margin-top: 10px; text-align: center; }

  .sb-cn-settings {
    background: none; border: none;
    font-size: 11.5px; color: rgba(10, 22, 40, 0.32);
    cursor: pointer; text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 0.15s; padding: 0;
  }
  .sb-cn-settings:hover { color: #0072ff; }

  @media (max-width: 420px) {
    .sb-cn-card { bottom: 16px; right: 16px; padding: 14px; }
    .sb-cn-actions { flex-direction: column; }
    .sb-cn-btn--primary, .sb-cn-btn--ghost { flex: unset; width: 100%; }
  }
`;